import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The Finance tab strip — one module for every Finance page and the two workspace pages that sit in it (design sync
 * 2026-10-09, Claude Design version 1791495439483263; the prototype's `ORG_PAGES` group `fin` and `finTabs`).
 *
 * The export's order: Overview (`/finance/funding`, "Funding" before), This workspace (`/billing`), Plans, Allowances
 * and caps, Documents, Postpaid, Payouts (`/payouts`). Billing and Payouts left the sidebar and became these two tabs,
 * so they carry the strip too, under a "<payer> · Finance" kicker.
 *
 * What is OFFERED follows the context (the API decides every read): the organization's tabs need an organization
 * role that reads its money (`account.finance.read`, as the deep-link check does); "This workspace" needs a workspace
 * open; "Payouts" a workspace role that reads the payout statement (`payout.read`). A tab the person could not open is
 * not drawn.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { authContextQuery } from "../../api/query.js";
import { memberCan } from "../../shell/member-capability.js";

import "../account-admin/page/organization-page.css";

export const FINANCE_TAB = [
  { id: "funding", get label() { return portalText("finance", "m1232"); }, to: "/finance/funding", reach: "organization" },
  { id: "billing", get label() { return portalText("finance", "m0165"); }, to: "/billing", reach: "workspace" },
  { id: "plans", get label() { return portalText("finance", "m1233"); }, to: "/finance/plans", reach: "organization" },
  { id: "allowances", get label() { return portalText("finance", "m0255"); }, to: "/finance/allowances", reach: "organization" },
  { id: "documents", get label() { return portalText("finance", "m1219"); }, to: "/finance/documents", reach: "organization" },
  { id: "postpaid", get label() { return portalText("finance", "m1234"); }, to: "/finance/postpaid", reach: "organization" },
  { id: "payouts", get label() { return portalText("finance", "m0812"); }, to: "/payouts", reach: "payout" },
] as const;
export type FinanceTabId = (typeof FINANCE_TAB)[number]["id"];

/** The tabs this context may open, in the export's order. */
export function useFinanceTab(): readonly (typeof FINANCE_TAB)[number][] {
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway)).data;
  if (context === undefined || context === null || context.kind === "selection_required") return [];
  const readsFinance = context.capability.account.includes("account.finance.read");
  const role = context.kind === "workspace_selected" ? context.memberRole : null;
  return FINANCE_TAB.filter((one) =>
    one.reach === "organization" ? readsFinance : one.reach === "workspace" ? role !== null : role !== null && memberCan(role, "payout.read"),
  );
}

/** The strip: the current tab is marked; nothing is drawn while the context is unknown. */
export function FinanceTabs(props: { readonly current: FinanceTabId; readonly className?: string }): ReactNode {
  usePortalTranslation("finance");
  const shown = useFinanceTab();
  if (shown.length === 0) return null;
  return (
    <nav className={props.className === undefined ? "op-tabs" : `op-tabs ${props.className}`} aria-label={portalText("finance", "m0515")}>
      {shown.map((one) => (
        <Link key={one.id} to={one.to} className={one.id === props.current ? "op-tab op-tab-on" : "op-tab"} aria-current={one.id === props.current ? "page" : undefined}>
          {one.label}
        </Link>
      ))}
    </nav>
  );
}

/**
 * "Roma Pizzeria S.r.l. · Finance" — the kicker above the strip on This workspace and Payouts: the legal payer's name
 * where the person may read the organization's billing profile, else the organization's name.
 */
export function FinanceKicker(props: { readonly className: string }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway)).data;
  const scoped = context === undefined || context === null || context.kind === "selection_required" ? null : context;
  const readsFinance = scoped?.capability.account.includes("account.finance.read") === true;
  const profile = useQuery({
    queryKey: ["billing-profile", scoped?.accountId ?? "none"],
    queryFn: () => gateway.readBillingProfile(scoped?.accountId ?? ""),
    enabled: scoped !== null && readsFinance,
  });
  if (scoped === null) return null;
  const legalName = profile.data === undefined || profile.data === null ? null : profile.data.legalName;
  return <p className={props.className}>{portalText("finance", "m0516", { value1: legalName ?? scoped.accountName })}</p>;
}
